// src/sim/hits.ts — Hit çözümü (ARCHITECTURE §4.4 birebir; DESIGN §5 formül bloğu, §6 yatan, §7 parry/blok): Active'teki hitbox'lar ×
// hurtbox'lar (bu tick'in sonuç konumları; grup başına bir isabet), parry → blok → zırh → formül (n/falloff/stale/kbFloor/DI/hitstun
// ×0.85^max(0,n−2)/tumble tavanı/zorunlu tumble) → durum (Launched/Tumble/Knockdown/Hitstun) → yan etkiler (hitstop ikili, silah düşürme,
// tutuş koparma, olaylar). `applyKnockback` (ApplyKnockbackFn) aynı çekirdek: uçan eşya (items.ts) ve fırlatılan beden (grab.ts) çağırır.
// Knockdown/tumble üreten isabet ayrıca `Ev.Downed` basar (sim gerçeği); DESIGN §10 mini ağır çekimini ondan view/slowMo.ts kurar.
// three'siz; sıcak yolda tahsis yok (scratch vektör + HIT parametre nesnesi).
import { Ev, FState, HitKind, MovePhase } from '../core/types.ts';
import type { Fighter, HitboxDef, MoveDef, SimWorld, Vec3 } from '../core/types.ts';
import { v3, yawOf } from '../core/vec3.ts';
import { clamp } from '../core/math.ts';
import { NO_ID, NO_TICK, RECENT_HITS_RING, STALE_QUEUE, TICK_HZ } from '../config/constants.ts';
import { T } from '../config/tuning.ts';
import { MOVE_ID } from '../config/moves.ts';
import { hitboxWorld, inCone, isLyingState, sphereHitsFighter } from './hitbox.ts';
import { applyBlockedHit, guardActive, inBlockCone, parryOpen } from './block.ts';
import { breakGrab, dropCarried, resolveThrownBodies, tryGrab } from './grab.ts';
import { enterHitstun, enterKnockdown, enterLaunched, enterTumble } from './knockdown.ts';

/** 2B uzunluk: Math.hypot V8'de FixedDoubleArray tahsis eder (sıcak yol yasağı; match.ts kalıbı) → sqrt(a²+b²). */
const hyp = (a: number, b: number): number => Math.sqrt(a * a + b * b);

const CENTER: Vec3 = v3();
const SQUASH_HIT = 3;   // fighter.ts SQUASH_HIT (paket dosyası import edilmez)

/** Vuruş parametreleri (tahsissiz; her çözümde yerinde doldurulur). */
interface HitParams {
  source: number; moveIdx: number; move: MoveDef | null;
  dirX: number; dirZ: number; kb: number; lift: number; dmg: number; stun: number; hitstop: number;
  heavy: boolean; knockdown: boolean; unblockable: boolean; guardCost: number; hitsLying: boolean;
  hx: number; hy: number; hz: number;
  /** Silah hitbox'ı sol elde mi (weapon.tip.l / weapon.mid.l → weaponItemL; dual W2 sol darbe) — dayanıklılık sayacı için. */
  leftHand: boolean;
}
const HIT: HitParams = {
  source: NO_ID, moveIdx: -1, move: null, dirX: 0, dirZ: 1, kb: 0, lift: 0, dmg: 0, stun: 0, hitstop: 0,
  heavy: false, knockdown: false, unblockable: false, guardCost: T.BLOCK.guardCostLight, hitsLying: true, hx: 0, hy: 0, hz: 0,
  leftHand: false,
};

/** Hurtbox'ı hiç açık olmayan durumlar (capabilities Hurtbox = 0). */
function hurtboxClosed(s: FState): boolean {
  return s === FState.Spawning || s === FState.Respawning || s === FState.Falling || s === FState.Eliminated;
}
/** Hedef tutan mı (Hold / Lifting / Carrying / PickingUp / omuz çarpması / Throwing) — tutuş koparma eşiği 1. */
function isHolder(b: Fighter): boolean { return b.grabTarget !== NO_ID; }
function isVictim(b: Fighter): boolean { return b.state === FState.Held || b.state === FState.Carried; }

/** n = hedefe son 84 t'deki isabet sayısı (bu isabet hariç). */
function recentHits(b: Fighter, tick: number): number {
  let n = 0;
  for (let i = 0; i < RECENT_HITS_RING; i++) { const t = b.recentHitTicks[i]!; if (t !== NO_TICK && tick - t <= T.COMBAT.comboWindowTicks) n++; }
  return n;
}
/** k = aynı hamlenin 150 t içindeki önceki isabetleri (kuyruk 6). */
function staleCount(b: Fighter, moveIdx: number, tick: number): number {
  if (moveIdx < 0) return 0;
  let k = 0;
  for (let i = 0; i < STALE_QUEUE; i++) if (b.staleMove[i] === moveIdx && tick - b.staleTick[i]! <= T.COMBAT.staleTicks) k++;
  return k;
}
function recordHit(b: Fighter, moveIdx: number, tick: number): void {
  b.recentHitTicks[b.recentHead] = tick; b.recentHead = (b.recentHead + 1) % RECENT_HITS_RING;
  if (moveIdx >= 0) { b.staleMove[b.staleHead] = moveIdx; b.staleTick[b.staleHead] = tick; b.staleHead = (b.staleHead + 1) % STALE_QUEUE; }
  b.comboOnMe = b.lastHitTick !== NO_TICK && tick - b.lastHitTick <= T.COMBAT.comboWindowTicks ? b.comboOnMe + 1 : 1;
  if (b.comboOnMe > b.longestComboOnMe) b.longestComboOnMe = b.comboOnMe;
}
/** Hitstun tick'i: max(6, round(60·clamp(0.12 + 0.020·kb, 0.12, 0.90)·0.85^max(0, n−2))). */
export function hitstunTicksFor(kbSpeed: number, n: number): number {
  const sec = clamp(T.COMBAT.hitstunBase + T.COMBAT.hitstunPerKb * kbSpeed, T.COMBAT.hitstunMin, T.COMBAT.hitstunMax);
  const mult = Math.pow(T.COMBAT.hitstunFalloff, Math.max(0, n - T.COMBAT.hitstunFalloffFrom));
  return Math.max(T.COMBAT.hitstunMinTicks, Math.round(TICK_HZ * sec * mult));
}
/** Hedefin bu tick'teki zırhı: armorUntil ∨ hamle zırh penceresi (moveTick) ∨ armorMinStun (gelen ham stun altında). */
function armored(b: Fighter, world: SimWorld, rawStun: number, tick: number): boolean {
  if (b.armorUntil > tick) return true;
  if ((b.state === FState.Attack) && b.moveIdx >= 0) {
    const m = world.moves[b.moveIdx]!;
    if (m.armor && b.moveTick >= m.armor[0] && b.moveTick <= m.armor[1]) return m.armorMinStun === undefined || rawStun < m.armorMinStun;
  }
  return false;
}

/**
 * Çekirdek (ARCHITECTURE §4.4 resolveHit adım 1–7). HIT doldurulmuş olmalı. Döner: isabet uygulandı mı (parry/blok dâhil "dokunma" = true).
 */
function resolveCore(world: SimWorld, b: Fighter): boolean {
  const tick = world.tick;
  const a = HIT.source >= 0 ? world.fighters[HIT.source] : undefined;
  const lying = isLyingState(b.state);
  if (lying && !HIT.hitsLying) return false;

  // 1. Parry (L1 dokunma penceresi; tutma hariç). Ön koni: parry = blok dokunuşu olduğundan bloğun konisine tabidir
  // (DESIGN §7 "arkadan gelen vuruş bloğu geçer"; parry için koni belirtilmemiş — 2v1'de sırttan bedava parry olmasın).
  // Arkadan gelen vuruş pencereyi KAPATMAZ: normal işlenir, dokunuş penceresi açık kalır (stepBlock kapatır).
  if (!HIT.unblockable && parryOpen(b, tick) && !lying && inBlockCone(b, HIT)) {
    b.parryUntil = NO_TICK;
    b.parryBonusUntil = tick + T.BLOCK.parryBonusTicks;
    b.controlLockUntil = Math.max(b.controlLockUntil, tick + T.BLOCK.parryFreeTicks);   // 15 t'de serbest
    b.hitstop = Math.max(b.hitstop, T.HITSTOP.parry);
    b.lastHitKind = HitKind.Parried;
    if (a && HIT.move) {   // yalnız hamle isabeti: mermi (eşya/beden) parry'sinde atan sersemlemez
      enterHitstun(world, a, T.BLOCK.parryVictimTicks, HitKind.Mid);   // saldıran Hit_B 0.67 s, kb 0, i-frame yok
      a.vel.x = 0; a.vel.z = 0;
      a.hitstop = Math.max(a.hitstop, T.HITSTOP.parry); a.stateUntil += T.HITSTOP.parry;
    }
    const e = world.events.push(Ev.Parry, tick, HIT.source, b.id); e.x = HIT.hx; e.y = HIT.hy; e.z = HIT.hz;
    const h = world.events.push(Ev.Hitstop, tick, HIT.source, b.id); h.v0 = T.HITSTOP.parry;
    return true;
  }

  // 2. Blok (ön koni; arkadan geçer); guard break → çarpanlar uygulanmaz
  let blocked = false, broke = false;
  if (!HIT.unblockable && !lying && guardActive(b, tick) && inBlockCone(b, HIT)) {
    broke = applyBlockedHit(world, b, HIT.source, HIT.guardCost);
    blocked = !broke;
  }

  // 4. Formül (DESIGN §5)
  const n = recentHits(b, tick);
  const k = staleCount(b, HIT.moveIdx, tick);
  const falloff = Math.pow(T.COMBAT.falloff, n);
  const stale = Math.max(T.COMBAT.staleMin, Math.pow(T.COMBAT.staleBase, k));
  const blockMult = blocked ? T.BLOCK.kbMult : 1;
  const dmgPct = b.damage / 100;
  let kbSpeed = HIT.kb * (1 + dmgPct * T.COMBAT.kbDmgScale) * falloff * stale * blockMult * b.weightMult * (world.arena.halfWidth / 9);
  if (!blocked && HIT.kb > 0) kbSpeed = Math.max(kbSpeed, T.COMBAT.kbFloor);
  let liftV = HIT.lift * (1 + dmgPct * T.COMBAT.liftDmgScale) * falloff * stale * blockMult;
  if (broke) liftV = 0;   // guard break: 48 t sersemlik yerde (kaldırma yok)
  const rawStun = HIT.stun * falloff * stale;
  let stunAdd = rawStun * (blocked ? T.BLOCK.stunMult : 1);
  if (blocked && b.hasShield) stunAdd = rawStun * T.BLOCK.chipStunShield;
  let dmgAdd = blocked ? 0 : HIT.dmg * stale;
  if (lying) { dmgAdd *= T.COMBAT.lyingDmgMult; stunAdd = 0; kbSpeed *= T.COMBAT.lyingKbMult; liftV = 0; }
  // DI: hedefin bu tick'teki çubuğunun dir'e dik bileşeni × 18°
  let dirX = HIT.dirX, dirZ = HIT.dirZ;
  const inp = world.inputs[b.id];
  if (inp && !blocked && !lying) {
    const px = -dirZ, pz = dirX;
    let s = inp.moveX * px + inp.moveZ * pz; if (s < -1) s = -1; else if (s > 1) s = 1;   // clamp yerinde (çağrı kutulamasın)
    if (s !== 0) { const ang = s * T.COMBAT.diDeg * Math.PI / 180; const c = Math.cos(ang), sn = Math.sin(ang); const nx = dirX * c - dirZ * sn, nz = dirX * sn + dirZ * c; dirX = nx; dirZ = nz; }
  }

  // 3. Zırh (hedefin): kesilmez, kb ×0.3, dmg/stun tam; knockdown kararı yine tetiklenir
  const arm = !blocked && !lying && armored(b, world, rawStun, tick);
  if (arm) { kbSpeed *= T.BLOCK.kbMult; liftV = 0; }

  // 5. Durum kararı
  const stunBefore = b.stun;
  b.stun = Math.min(T.COMBAT.stunMax, b.stun + stunAdd);
  b.damage = Math.min(T.COMBAT.dmgMax, b.damage + dmgAdd);
  // parryBonus SALDIRANINDIR (DESIGN §7: "savunan … 120 t içindeki İLK İSABETİ knockdown"): ödül parry yapanın vuruşuna aittir,
  // yediği vuruşa değil. Mermi/fırlatma (a yok) bonus taşımaz.
  const bonus = a !== undefined && a.parryBonusUntil > tick;
  let tumble = !blocked && !lying && (kbSpeed >= T.COMBAT.tumbleKb || HIT.knockdown || bonus || n >= T.COMBAT.forcedTumbleHits);
  if (tumble) {
    if (bonus && a !== undefined) a.parryBonusUntil = NO_TICK;   // tek kullanımlık; b'nin alanı korunur (animMap 15 t serbest-kalma görseli)
    kbSpeed = Math.min(kbSpeed, T.COMBAT.tumbleCapBase + T.COMBAT.tumbleCapPerDmg * dmgPct);
  }
  const knockdown = !blocked && !lying && (b.stun >= T.COMBAT.knockdownStun || (stunBefore >= T.COMBAT.heavyKnockdownStun && HIT.heavy));
  let hitstun = hitstunTicksFor(kbSpeed, n);
  // DESIGN §4.1/§7: tutma denemesi (G1 w/a) sırasında vurulan tutan → "tutma bozulur, tutan Hit_A 0.30 s" (18 t; jab 13 t'den uzun)
  if (b.state === FState.GrabAttempt && !blocked) hitstun = Math.max(hitstun, T.GRAB.grabbedHolderStunTicks);

  // tutuş koparma (DESIGN §6): tutana herhangi isabet (≥ 1), kurbana ≥ 20
  let holderHitstunMin = 0;
  if (isHolder(b) && stunAdd >= T.GRAB.holderBreakStun) { breakGrab(world, b, 0); holderHitstunMin = T.GRAB.holderStunTicks; }
  else if (isVictim(b)) {
    if (stunAdd >= T.GRAB.breakStun) { const h = world.fighters[b.grabbedBy]; if (h) breakGrab(world, h, 1); }
    else {
      // eşik altı: dmg/stun işlendi, knockback yok, kurban sokette kalır
      recordHit(b, HIT.moveIdx, tick); b.lastHitBy = HIT.source >= 0 ? HIT.source : b.lastHitBy; b.lastHitTick = tick;
      const e = world.events.push(Ev.Hit, tick, HIT.source, b.id); e.v0 = 0; e.v1 = dmgAdd; e.v2 = HitKind.Light; e.x = HIT.hx; e.y = HIT.hy; e.z = HIT.hz; e.move = HIT.moveIdx;
      return true;
    }
  }

  // KB yönü (DI sonrası) hedefe yazılır: enterTumble buradan okur (double argüman kutulaması yok); blokta da view'a yön gerekir
  b.lastHitDirX = dirX; b.lastHitDirZ = dirZ;
  // hız (isabet anında havada mıydı: durum kararı bunu kullanır; lift yerdekini havalandırsa da Hitstun kalır)
  const airborne = !b.grounded;
  if (!lying) {
    b.vel.x = dirX * kbSpeed + T.COMBAT.velCarry * b.vel.x; b.vel.z = dirZ * kbSpeed + T.COMBAT.velCarry * b.vel.z;
    b.vel.y = Math.max(b.vel.y, 0) + liftV;
    if (liftV > 0 && b.grounded) { b.grounded = false; b.lastGroundedTick = tick; }
  } else {
    b.vel.x = dirX * kbSpeed; b.vel.z = dirZ * kbSpeed;   // yatana kayma (τ 0.36 knockdown.ts); Lying süresi uzamaz
  }

  let kind: HitKind;
  let directKnockdown = false;   // enterKnockdown kendi çakılma hitstop'unu süreye ekler → aşağıda ikinci kez eklenmesin
  if (blocked) {
    kind = HitKind.Blocked;
    b.vel.x = dirX * kbSpeed; b.vel.z = dirZ * kbSpeed;   // kb ×0.3 (velCarry yok)
    if (a && HIT.move && HIT.move.family === 'dash') { enterHitstun(world, a, T.GRAB.grabbedHolderStunTicks, HitKind.Light); a.vel.x = 0; a.vel.z = 0; }   // Dash Tackle bloklandı: Hit_A 0.30 s
  } else if (lying) {
    kind = HitKind.Lying;
  } else if (arm && !knockdown) {
    kind = kbSpeed < T.COMBAT.hitAKb ? HitKind.Light : HitKind.Mid;   // zırh: yalnız additive flinch (view)
  } else if (airborne && !tumble && !knockdown) {
    enterLaunched(world, b, hitstun); kind = HitKind.Mid;   // havada isabet: Death_A donuk + pitch
  } else if (tumble || (knockdown && airborne)) {
    enterTumble(world, b, hitstun, false); kind = knockdown ? HitKind.Finisher : HitKind.Tumble;
  } else if (knockdown) {
    if (liftV > 0) { enterTumble(world, b, hitstun, false); } else { enterKnockdown(world, b, false); directKnockdown = true; }
    kind = HitKind.Finisher;
  } else {
    // yerde isabet (lift'li de olsa) Hitstun: hook lift 4.3 → 0.29 s havada, hitstun bitince Air → iniş Idle ("ayakta iner"); Launched yalnız havada isabet
    enterHitstun(world, b, Math.max(hitstun, holderHitstunMin), kbSpeed < T.COMBAT.hitAKb ? HitKind.Light : HitKind.Mid);
    kind = b.lastHitKind as HitKind;
    // arkadan ağır darbe: kurban saldırana döner (5 t; sim'de anında)
    if (HIT.heavy && (dirX * Math.sin(b.yaw) + dirZ * Math.cos(b.yaw)) > 0.5) b.yaw = Math.atan2(-dirX, -dirZ);   // yawOf yerinde (dir birim)
  }
  if (broke && b.state === FState.Hitstun) { b.stateUntil = Math.max(b.stateUntil, tick + T.BLOCK.breakTicks); b.hitstunTicks = b.stateUntil - tick; }
  if (!blocked && holderHitstunMin > 0 && b.state === FState.Hitstun) b.stateUntil = Math.max(b.stateUntil, tick + holderHitstunMin);

  // 6. Yan etkiler
  const stop = Math.max(blocked ? T.HITSTOP.block : HIT.hitstop, (tumble || knockdown) && !blocked ? T.HITSTOP.finisher : 0);
  b.hitstop = Math.max(b.hitstop, stop);
  // hitstop iki tarafı eşit dondurur (DESIGN §1/§5 zincir kanıtı): saldıranın moveTick'i durur, kurbanın hitstun/tumble/knockdown süresi de durur.
  // directKnockdown hariç: enterKnockdown çakılma hitstop'unu stateUntil'e ZATEN ekledi (DESIGN §6: 11 t + 7 t = 18 t; ikinci ekleme 25 t yapardı).
  if (stop > 0 && !directKnockdown && b.stateUntil !== NO_TICK && (b.state === FState.Hitstun || b.state === FState.Launched || b.state === FState.Tumble || b.state === FState.Knockdown)) b.stateUntil += stop;
  if (a && HIT.move) {   // mermide atan donmaz
    a.hitstop = Math.max(a.hitstop, stop); a.touched = true; if (!blocked && a.firstHitTick === NO_TICK) a.firstHitTick = a.moveTick;
    // DESIGN §9 silah dayanıklılığı: her silah isabeti (blok dâhil) +1; 10'da items.step kırar (ItemApi'ye ek gerekmez). Sol el hitbox'ı → weaponItemL.
    if (HIT.move.family === 'weapon') {
      const wid = HIT.leftHand && a.weaponItemL >= 0 ? a.weaponItemL : a.weaponItem;
      if (wid >= 0) { const it = world.items[wid]; if (it && it.state === 'held') it.hits++; }
    }
  }
  if (!blocked) {
    recordHit(b, HIT.moveIdx, tick);
    b.lastHitBy = HIT.source >= 0 ? HIT.source : b.lastHitBy; b.lastHitTick = tick;
    b.lastHitKind = kind;
    b.flashUntil = tick + Math.round(T.FEEL.flashMs / 1000 * TICK_HZ);
    b.squashUntil = tick + Math.round(T.FEEL.squashMs / 1000 * TICK_HZ); b.squashKind = SQUASH_HIT;
    // "Bu isabet rakibi yere serdi / takla attırdı" — SİM GERÇEĞİ olarak bildirilir (Ev.Downed). DESIGN §10 mini ağır
    // çekimi bundan tetiklenir ama kararı (kim izliyor, ne sıklıkta) view/slowMo.ts verir; sim yavaşlamaz, hash değişmez.
    // Ayrı olay gerekti çünkü Ev.Hit'in HitKind'ı tetiği birebir taşımıyor: yatan kurbana vuruşta kind = Lying olur
    // ama knockdown/tumble üretilmez, armor'lu takla ise Mid görünür (hits.ts:155/160 !lying kapıları).
    if (knockdown || tumble) { const d = world.events.push(Ev.Downed, tick, HIT.source, b.id); d.v0 = knockdown ? 1 : 0; d.v1 = tumble ? 1 : 0; }
    if (!arm && (knockdown || tumble || stunAdd >= T.COMBAT.weaponDropStun)) world.systems.items.drop(world, b, tick, Ev.Hit);
    // omuz çarpması isabeti: kurban düşer (Lying 48 t) — hits.ts → grab.ts
    if (a && HIT.move && HIT.move.id === MOVE_ID.C_BASH && a.grabTarget !== NO_ID) {
      const v = world.fighters[a.grabTarget]; if (v && v !== b) dropCarried(world, a, T.KNOCKDOWN.carryDropLyingBash);
    }
    // Flying Kick isabette −3 geri seker
    if (a && HIT.move && HIT.move.id === MOVE_ID.A2) { const s = Math.sin(a.yaw), c = Math.cos(a.yaw); a.vel.x = -s * 3; a.vel.z = -c * 3; }
    const e = world.events.push(Ev.Hit, tick, HIT.source, b.id);
    e.v0 = kbSpeed; e.v1 = dmgAdd; e.v2 = kind; e.x = HIT.hx; e.y = HIT.hy; e.z = HIT.hz; e.dx = dirX; e.dz = dirZ; e.move = HIT.moveIdx;
  } else {
    b.lastHitKind = HitKind.Blocked;
  }
  if (stop > 0) { const h = world.events.push(Ev.Hitstop, tick, HIT.source, b.id); h.v0 = stop; }
  return true;
}
/** Hamle hitbox'ı için parametreleri doldurur. */
function fillFromMove(a: Fighter, m: MoveDef, hb: HitboxDef, b: Fighter): void {
  const o = hb.override;
  HIT.source = a.id; HIT.moveIdx = a.moveIdx; HIT.move = m;
  HIT.kb = o ? o.kb : m.kb; HIT.lift = o ? o.lift : m.lift; HIT.dmg = o ? o.dmg : m.dmg; HIT.stun = o ? o.stun : m.stun;
  HIT.hitstop = m.hitstop; HIT.heavy = m.heavy; HIT.knockdown = m.knockdown === true; HIT.unblockable = m.unblockable === true;
  HIT.guardCost = m.guardCost; HIT.hitsLying = hb.hitsLying ?? m.hitsLying ?? false;
  HIT.leftHand = hb.joint === 'weapon.tip.l' || hb.joint === 'weapon.mid.l';
  HIT.hx = CENTER.x; HIT.hy = CENTER.y; HIT.hz = CENTER.z;
  const dx = b.pos.x - a.pos.x, dz = b.pos.z - a.pos.z;
  const d = hyp(dx, dz);
  if (d > 1e-4) { HIT.dirX = dx / d; HIT.dirZ = dz / d; } else { HIT.dirX = Math.sin(a.yaw); HIT.dirZ = Math.cos(a.yaw); }
}

/** SimSystems.applyKnockback (ApplyKnockbackFn): uçan eşya / fırlatılan beden; hafif, knockdown'sız, bloklanır (kb ×0.3), guard −12. */
export function applyKnockback(world: SimWorld, target: Fighter, dirX: number, dirZ: number, kb: number, lift: number,
  dmg: number, stun: number, hitstop: number, source: number): void {
  if (hurtboxClosed(target.state) || target.invulnUntil > world.tick || target.spawnProtUntil > world.tick) return;
  const d = hyp(dirX, dirZ);
  HIT.source = source; HIT.moveIdx = -1; HIT.move = null;
  HIT.dirX = d > 1e-6 ? dirX / d : Math.sin(target.yaw + Math.PI); HIT.dirZ = d > 1e-6 ? dirZ / d : Math.cos(target.yaw + Math.PI);
  HIT.kb = kb; HIT.lift = lift; HIT.dmg = dmg; HIT.stun = stun; HIT.hitstop = hitstop;
  HIT.heavy = false; HIT.knockdown = false; HIT.unblockable = false; HIT.guardCost = T.BLOCK.guardCostLight; HIT.hitsLying = true;
  HIT.leftHand = false;
  HIT.hx = target.pos.x; HIT.hy = target.pos.y + 1.0; HIT.hz = target.pos.z;
  resolveCore(world, target);
}

/** Hitbox × hedef döngüsü (§4.4 dış döngü). */
function resolveAttacker(world: SimWorld, a: Fighter): void {
  const tick = world.tick;
  const m = world.moves[a.moveIdx];
  if (!m) return;
  const hbs = m.hitboxes;
  for (let h = 0; h < hbs.length; h++) {
    const hb = hbs[h]!;
    if (a.moveTick < hb.from || a.moveTick > hb.to) continue;
    const r = hitboxWorld(CENTER, world, a, hb);
    const group = hb.group ?? 0;
    for (let i = 0; i < world.fighters.length; i++) {
      const b = world.fighters[i]!;
      if (b === a || b.grabbedBy === a.id) continue;   // tutan kendi kurbanını (sokette) vurmaz
      if (hurtboxClosed(b.state) || b.spawnProtUntil > tick) continue;
      if ((a.hitMask[group]! & (1 << b.id)) !== 0) continue;
      if (isLyingState(b.state) && !(hb.hitsLying ?? m.hitsLying)) continue;
      if (hb.groundedOnly && !b.grounded) continue;
      if (!sphereHitsFighter(CENTER, r, b)) continue;
      if (hb.cone !== undefined && !inCone(a, b, hb.cone)) continue;
      if (b.invulnUntil > tick) continue;
      if (m.family === 'grab') {
        // G1: hitbox teması = tutma denemesi (bloğu yener; ayak > 0.5 tutulamaz)
        a.hitMask[group] = a.hitMask[group]! | (1 << b.id);
        if (tryGrab(world, a, b)) return;   // tutuş kuruldu: hamle bitti
        continue;
      }
      fillFromMove(a, m, hb, b);
      a.hitMask[group] = a.hitMask[group]! | (1 << b.id);
      resolveCore(world, b);
      if (a.state !== FState.Attack && a.state !== FState.GrabAttempt) return;   // parry/trade: saldıran kesildi
    }
  }
}

/** SimSystems.resolveHits — ARCHITECTURE §3 adım 7. */
export function resolveHits(world: SimWorld): void {
  const fs = world.fighters;
  for (let i = 0; i < fs.length; i++) {
    const a = fs[i]!;
    if (a.hitstop > 0 || a.moveIdx < 0 || a.phase !== MovePhase.Active) continue;
    if (a.state !== FState.Attack && a.state !== FState.GrabAttempt) continue;
    resolveAttacker(world, a);
  }
  resolveThrownBodies(world);
}

